import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Users, Loader2, CalendarDays, X } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const inp = "border-zinc-800 bg-zinc-950 text-[13px] text-zinc-100";
const toLocalInput = (iso) => { if (!iso) return ""; const d = new Date(iso); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16); };
const fmt = (iso) => new Date(iso).toLocaleString("hu-HU", { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
const BLANK = { title: "", description: "", location: "", platform: "", agents: [], starts_at: "", ends_at: "", capacity: "", registration_open: true, published: true };

function EventForm({ ev, users, onClose, onSaved }) {
  const [f, setF] = useState(ev ? { ...ev, starts_at: toLocalInput(ev.starts_at), ends_at: toLocalInput(ev.ends_at), capacity: ev.capacity || "" } : BLANK);
  const [busy, setBusy] = useState(false);
  const s = (patch) => setF((x) => ({ ...x, ...patch }));
  const toggleAgent = (n) => s({ agents: f.agents.includes(n) ? f.agents.filter((a) => a !== n) : [...f.agents, n] });
  const save = async () => {
    setBusy(true);
    try {
      const body = { ...f, starts_at: new Date(f.starts_at).toISOString(), ends_at: new Date(f.ends_at).toISOString(), capacity: f.capacity ? Number(f.capacity) : null };
      if (ev) await api.put(`/admin/events/${ev.id}`, body); else await api.post("/admin/events", body);
      toast.success("Esemény mentve");
      onSaved();
    } catch (e) { toast.error(apiErr(e?.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-zinc-800 bg-[#111113] text-zinc-100 sm:max-w-xl" data-testid="event-form">
        <DialogHeader><DialogTitle>{ev ? "Esemény szerkesztése" : "Új esemény"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input data-testid="event-title" value={f.title} onChange={(e) => s({ title: e.target.value })} placeholder="Cím" className={inp} />
          <Textarea data-testid="event-description" value={f.description} onChange={(e) => s({ description: e.target.value })} placeholder="Rövid leírás" className={`${inp} min-h-[80px]`} />
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-[12px] text-zinc-500">Kezdés (helyi idő)<Input data-testid="event-start" type="datetime-local" value={f.starts_at} onChange={(e) => s({ starts_at: e.target.value })} className={`${inp} mt-1`} /></label>
            <label className="text-[12px] text-zinc-500">Befejezés (helyi idő)<Input data-testid="event-end" type="datetime-local" value={f.ends_at} onChange={(e) => s({ ends_at: e.target.value })} className={`${inp} mt-1`} /></label>
            <Input data-testid="event-location" value={f.location} onChange={(e) => s({ location: e.target.value })} placeholder="Helyszín (pl. Online – Zoom, Budapest…)" className={inp} />
            <Input data-testid="event-platform" value={f.platform} onChange={(e) => s({ platform: e.target.value })} placeholder="Platform (pl. Roblox, Discord)" className={inp} />
            <Input data-testid="event-capacity" type="number" min={1} value={f.capacity} onChange={(e) => s({ capacity: e.target.value })} placeholder="Férőhely (üres = korlátlan)" className={inp} />
          </div>
          <div>
            <div className="mb-1.5 text-[12px] text-zinc-500">Résztvevő agentek</div>
            <div className="flex flex-wrap gap-1.5" data-testid="event-agents">
              {users.map((u) => (
                <button key={u.id} type="button" onClick={() => toggleAgent(u.name)} data-testid={`event-agent-${u.id}`}
                  className={`rounded-full border px-2.5 py-1 text-[12px] ${f.agents.includes(u.name) ? "border-blue-500/60 bg-blue-500/15 text-blue-200" : "border-zinc-800 text-zinc-400"}`}>{u.name}</button>
              ))}
            </div>
          </div>
          <div className="flex flex-wrap gap-5 text-[13px] text-zinc-300">
            <label className="flex items-center gap-2"><Switch data-testid="event-published" checked={f.published} onCheckedChange={(v) => s({ published: v })} /> Publikus</label>
            <label className="flex items-center gap-2"><Switch data-testid="event-reg-open" checked={f.registration_open} onCheckedChange={(v) => s({ registration_open: v })} /> Regisztráció nyitva</label>
          </div>
          <button data-testid="event-save" onClick={save} disabled={busy || !f.title.trim() || !f.starts_at || !f.ends_at}
            className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-md bg-blue-600 text-[13px] font-semibold text-white hover:bg-blue-500 disabled:opacity-40">
            {busy && <Loader2 size={14} className="animate-spin" />} Mentés
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Registrations({ ev, onClose }) {
  const [rows, setRows] = useState(null);
  const load = () => api.get(`/admin/events/${ev.id}/registrations`).then((r) => setRows(r.data));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const del = async (r) => { await api.delete(`/admin/events/${ev.id}/registrations/${r.id}`); load(); };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="border-zinc-800 bg-[#111113] text-zinc-100 sm:max-w-lg" data-testid="event-registrations">
        <DialogHeader><DialogTitle>Regisztráltak · {ev.title}</DialogTitle></DialogHeader>
        {!rows ? <Loader2 className="animate-spin text-zinc-500" /> : rows.length === 0 ? <p className="text-[13px] text-zinc-500">Még senki nem regisztrált.</p> : (
          <div className="max-h-[60vh] divide-y divide-zinc-800 overflow-y-auto rounded-md border border-zinc-800">
            {rows.map((r) => (
              <div key={r.id} className="flex items-center gap-3 px-3 py-2 text-[13px]" data-testid={`event-reg-${r.id}`}>
                <span className="flex-1 truncate text-zinc-200">{r.email}</span>
                <span className="text-[11px] uppercase text-zinc-500">{r.lang}</span>
                <span className="text-[12px] text-zinc-500">{fmt(r.created_at)}</span>
                <button onClick={() => del(r)} className="text-zinc-500 hover:text-red-400"><X size={14} /></button>
              </div>
            ))}
          </div>
        )}
        {rows?.length > 0 && <button onClick={() => navigator.clipboard.writeText(rows.map((r) => r.email).join(", ")).then(() => toast.success("Email-címek másolva"))} className="text-[12px] text-blue-400 hover:underline">Összes email-cím másolása</button>}
      </DialogContent>
    </Dialog>
  );
}

export function ModEvents() {
  const [list, setList] = useState(null);
  const [users, setUsers] = useState([]);
  const [edit, setEdit] = useState(null);
  const [regs, setRegs] = useState(null);
  const load = () => api.get("/admin/events").then((r) => setList(r.data));
  useEffect(() => {
    load();
    api.get("/admin/users").then((r) => setUsers(r.data.filter((u) => u.active !== false)));
  }, []);
  const del = async (ev) => {
    if (!window.confirm(`Törlöd: ${ev.title}? A regisztrációk is törlődnek.`)) return;
    await api.delete(`/admin/events/${ev.id}`);
    load();
  };
  const now = new Date().toISOString();
  return (
    <section className="space-y-3" data-testid="mod-events">
      <div className="flex items-center gap-3">
        <CalendarDays size={18} className="text-[#FFCC00]" />
        <h2 className="text-lg font-semibold text-zinc-50">Események</h2>
        <span className="text-[12px] text-zinc-500">A nyilvános Elérhetőség oldalon jelennek meg</span>
        <button data-testid="event-new" onClick={() => setEdit("new")} className="ml-auto inline-flex h-9 items-center gap-1.5 rounded-md bg-blue-600 px-4 text-[13px] font-semibold text-white hover:bg-blue-500"><Plus size={14} /> Új esemény</button>
      </div>
      {!list ? <Loader2 className="animate-spin text-zinc-500" /> : list.length === 0 ? <p className="rounded-xl border border-zinc-800 p-5 text-[13px] text-zinc-500">Még nincs esemény.</p> : (
        <div className="divide-y divide-zinc-800/70 overflow-hidden rounded-xl border border-zinc-800/70 bg-[#111113]">
          {list.map((ev) => (
            <div key={ev.id} className={`flex flex-wrap items-center gap-3 px-4 py-3 ${ev.ends_at < now ? "opacity-50" : ""}`} data-testid={`admin-event-${ev.id}`}>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2 text-[14px] font-semibold text-zinc-100">{ev.title}
                  {!ev.published && <span className="rounded bg-zinc-800 px-1.5 text-[10px] font-normal text-zinc-400">Rejtett</span>}
                  {!ev.registration_open && <span className="rounded bg-zinc-800 px-1.5 text-[10px] font-normal text-zinc-400">Regisztráció zárva</span>}
                  {ev.ends_at < now && <span className="rounded bg-zinc-800 px-1.5 text-[10px] font-normal text-zinc-400">Lezajlott</span>}
                </div>
                <div className="text-[12px] text-zinc-500">{fmt(ev.starts_at)} – {fmt(ev.ends_at)}{ev.location && ` · ${ev.location}`}{ev.platform && ` · ${ev.platform}`}{ev.agents?.length ? ` · ${ev.agents.join(", ")}` : ""}</div>
              </div>
              <button data-testid={`admin-event-regs-${ev.id}`} onClick={() => setRegs(ev)} className="inline-flex items-center gap-1 rounded-md border border-zinc-800 px-2.5 py-1 text-[12px] text-zinc-300 hover:bg-zinc-900"><Users size={13} /> {ev.registered}{ev.capacity ? `/${ev.capacity}` : ""}</button>
              <button data-testid={`admin-event-edit-${ev.id}`} onClick={() => setEdit(ev)} className="text-zinc-400 hover:text-zinc-100"><Pencil size={15} /></button>
              <button data-testid={`admin-event-delete-${ev.id}`} onClick={() => del(ev)} className="text-zinc-500 hover:text-red-400"><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
      )}
      {edit && <EventForm ev={edit === "new" ? null : edit} users={users} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
      {regs && <Registrations ev={regs} onClose={() => setRegs(null)} />}
    </section>
  );
}
